import type { ChainOfCustodyItem } from '@/apis/types';
import { CoCEventType } from '@/common/enum';
import type { Extent } from './extent';
import type { LinearScale } from './layout';

/** One carrier scan as the charts draw it. */
export interface ShippingEvent {
  id: string;
  at: number;
  action: string;
  location: string;
}

/** The pointer must be this close to an event line to show its card (PRD §4.1). */
export const EVENT_HOVER_PX = 5;

/**
 * The shipment's carrier scans that carry an instant, oldest first. As in the old vitals modal, a custody row
 * without a location is left out: that's how the API shows manual team actions.
 */
export const shippingEvents = (
  chainOfCustody: readonly ChainOfCustodyItem[]
): ShippingEvent[] =>
  chainOfCustody
    .flatMap((item) => {
      const location = item.location?.trim() ?? '';
      return item.type === CoCEventType.CustodyEvent &&
        location &&
        typeof item.timestamp_ms === 'number'
        ? [
            {
              id: item.id,
              at: item.timestamp_ms,
              action: item.action?.trim() ?? '',
              location,
            },
          ]
        : [];
    })
    .sort((a, b) => a.at - b.at);

/** The events inside `window`, inclusive. */
export const eventsIn = (
  events: readonly ShippingEvent[],
  [from, to]: Extent
): ShippingEvent[] => events.filter(({ at }) => at >= from && at <= to);

/** The event whose line is closest to the pointer at `x` px, if it's within {@link EVENT_HOVER_PX}. */
export const eventNear = (
  events: readonly ShippingEvent[],
  x: number,
  xScale: LinearScale
): ShippingEvent | null => {
  let best: ShippingEvent | null = null;
  let bestGap = EVENT_HOVER_PX;
  for (const event of events) {
    const gap = Math.abs(xScale(event.at) - x);
    if (gap <= bestGap) {
      best = event;
      bestGap = gap;
    }
  }
  return best;
};
